import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { ToastProvider, ToastWrapper } from "./context.tsx";
import { state, Toast } from "./state.ts";
import * as styles from "./Toaster.module.less";
import { type ToastOptions } from "./types.ts";

/** A stray keystroke must not swallow an award before it has been seen. */
const DISMISS_GRACE = 400;

export function Toaster(): ReactNode {
  const [toasts, setToasts] = useState(state.toasts);
  useEffect(() => state.listen(setToasts), [toasts]);
  const wanted = toasts.some(({ options }) => options.backdrop);
  const [backdrop, setBackdrop] = useState(false);
  useEffect(() => {
    if (!wanted) {
      setBackdrop(false);
      return;
    }
    setBackdrop(true);
    // Typing clears the BLUR, never the award. The learner gets the lesson back
    // the instant they reach for the keyboard, and the award still gets its
    // full time on screen instead of being wiped out by the first keystroke of
    // the next lesson. Events are only observed, never swallowed, so that
    // keystroke still types.
    const armedAt = Date.now() + DISMISS_GRACE;
    const clear = () => {
      if (Date.now() >= armedAt) {
        setBackdrop(false);
      }
    };
    const options = { capture: true, passive: true } as const;
    document.addEventListener("keydown", clear, options);
    document.addEventListener("mousedown", clear, options);
    return () => {
      document.removeEventListener("keydown", clear, options);
      document.removeEventListener("mousedown", clear, options);
    };
  }, [wanted]);
  return (
    <>
      {/* A sibling rather than a child: the toaster is its own stacking layer,
          so a backdrop inside it would end up in front of the awards. */}
      {backdrop && <div className={styles.backdrop} aria-hidden="true" />}
      <div className={styles.toaster} hidden={toasts.length === 0}>
        {[...toasts].reverse().map((toast) => (
          <ToastProvider key={toast.key} toast={toast}>
            <ToastWrapper>{toast.message}</ToastWrapper>
          </ToastProvider>
        ))}
      </div>
    </>
  );
}

export function toast(
  message: ReactElement<any>,
  {
    autoClose = 3000,
    pauseOnHover = true,
    closeOnClick = true,
    backdrop = false,
  }: Partial<ToastOptions> = {},
): void {
  state.add(
    new Toast(message, { autoClose, pauseOnHover, closeOnClick, backdrop }),
  );
}
